import { apiClient, Conversation, dataManager, GRAPH_API_PREFIX, Project, Task, TypeId } from '@sdk';
import { writeProjectMapping } from './useProjectMapping';

export interface ApplyProjectResult {
  /** True when at least one entity was actually written. */
  saved: boolean;
  /** True when the conversation already had a *different* `project_id` and we
   *  replaced it. Drives the "navigate to the new project's home" UX after a
   *  remap (Feature 1). */
  wasReplacement: boolean;
}

/**
 * Resolve a Project's filesystem workdir (``fs_storage_mount_path``) by
 * looking it up directly from the Project entity — NOT from
 * ``task.project_root``, which is a denormalized cache that auto-derived
 * tasks (Plan/spec-derive flow) leave null even after ``project_id`` is
 * stamped.
 *
 * Returns ``undefined`` when ``projectId`` is missing, the project can't be
 * loaded, or it has no ``fs_storage_mount_path``. Callers use the undefined
 * branch to gate "Map this conversation to a local project first" toasts.
 */
export async function resolveWorkdir(
  projectId: string | null | undefined,
): Promise<string | undefined> {
  if (!projectId) return undefined;
  const project = await dataManager
    .getByTypeId<Project>(new TypeId(Project.type, projectId))
    .catch(() => null);
  return project?.fs_storage_mount_path ?? undefined;
}

/**
 * Stamp a chosen Project onto a Task and its Conversation. Idempotent —
 * calling repeatedly with the same project just rewrites the same fields.
 *
 * Writes:
 *   - `task.project_id` (FK)
 *   - `task.project_name` / `task.project_root` (annotations)
 *   - `conversation.project_id` (top-level on Conversation)
 *
 * Returns `wasReplacement=true` when the conversation already had a different
 * project — the caller can use this to navigate to the new project's home.
 */
export async function applyProjectToTask(taskId: string, project: Project): Promise<ApplyProjectResult> {
  if (!taskId) return { saved: false, wasReplacement: false };
  const task = await dataManager
    .getByTypeId<Task>(new TypeId(Task.type, taskId))
    .catch(() => null);
  if (!task) return { saved: false, wasReplacement: false };

  const previous = {
    project_id: task.project_id,
    project_name: task.project_name,
    project_root: task.project_root,
  };
  const newId = project.id ?? null;
  let saved = false;
  if (task.project_id !== newId) { task.project_id = newId; saved = true; }
  const newName = project.name ?? '';
  if ((task.project_name ?? '') !== newName) { task.project_name = newName; saved = true; }
  const newRoot = project.fs_storage_mount_path ?? '';
  if ((task.project_root ?? '') !== newRoot) { task.project_root = newRoot; saved = true; }
  if (saved) {
    try {
      await task.save();
    } catch (error) {
      task.project_id = previous.project_id;
      task.project_name = previous.project_name;
      task.project_root = previous.project_root;
      throw error;
    }
  }

  // Mirror onto the bound conversation so the conv-side fields (used by the
  // page loader and the gate) stay in sync.
  let wasReplacement = false;
  const convTypeId = task.firstContextOfType('conversation');
  if (convTypeId) {
    const r = await applyProjectToConversation(convTypeId.id, project);
    saved = saved || r.saved;
    wasReplacement = r.wasReplacement;
  }
  return { saved, wasReplacement };
}

async function saveConversationProjectLocally(conv: Conversation): Promise<Conversation> {
  if (!conv.id) throw new Error('Conversation is missing id');
  const updatedEntityJson = await apiClient.put<unknown>(
    `${GRAPH_API_PREFIX}/${Conversation.type}/${conv.id}`,
    conv.toJSON(),
  );
  if (!updatedEntityJson) throw new Error('No data returned');
  return dataManager.updateEntityFromJson<Conversation>(updatedEntityJson);
}

/**
 * Stamp a chosen Project onto a Conversation. Used for task-less conversations
 * (project-scoped or hub-direct chats) where there's no task to anchor the
 * mapping. Sets `conversation.project_id`. Idempotent. Returns
 * `wasReplacement=true` when the conversation previously pointed at a
 * different project.
 */
export async function applyProjectToConversation(
  conversationId: string,
  project: Project,
): Promise<ApplyProjectResult> {
  if (!conversationId) return { saved: false, wasReplacement: false };
  const conv = await dataManager
    .getByTypeId<Conversation>(new TypeId(Conversation.type, conversationId))
    .catch(() => null);
  if (!conv) return { saved: false, wasReplacement: false };
  const previous = conv.project_id ?? null;
  const next = project.id ?? null;
  if (previous === next) return { saved: false, wasReplacement: false };
  const wasReplacement = !!previous && !!next;
  conv.project_id = next;
  try {
    // `project_id` is a receiver-local mapping. Saving it through the generic
    // remote entity path adds Hub-Reflect and fails when the current user is
    // only a conversation member; the chip is projected from this field.
    await saveConversationProjectLocally(conv);
    return { saved: true, wasReplacement };
  } catch (error) {
    conv.project_id = previous;
    throw error;
  }
}

/**
 * Persist a remote→local project mapping in the per-machine mapping table
 * so that future messages tagged with the same `remote_project_id` auto-route
 * to the picked local project without re-prompting. No-op when either id
 * is missing.
 */
export async function persistRemoteToLocalMapping(
  remoteProjectId: string | null | undefined,
  localProjectId: string | null | undefined,
): Promise<void> {
  if (!remoteProjectId || !localProjectId) return;
  try {
    await writeProjectMapping(remoteProjectId, localProjectId);
  } catch {
    // non-fatal — the user can re-pick later if it didn't stick.
  }
}
